import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  approveCleanEvaluation,
  approveCleanSheet,
  createCleanEvaluation,
  createCleanSheet,
  deleteCleanEvaluation,
  deleteCleanSheet,
  fetchCleanEvaluation,
  fetchCleanEvaluations,
  fetchCleanSheet,
  fetchCleanSheets,
  revokeCleanEvaluation,
  revokeCleanSheet,
  updateCleanEvaluation,
  updateCleanSheet,
  downloadCleanSheetPdf,
  downloadCleanEvaluationPdf,
} from '../../api/cleanActivity';
import { notifyError, reportError } from '../../utils/notify';
import { fetchMyProfile } from '../../api/auth';
import { useCompanyStore } from '../../store/companyStore';
import { canConfirmInspection } from '../../types/companyRole';
import {
  APPROVAL_TARGET_CLEAN_ACTIVITY_EVALUATION,
  APPROVAL_TARGET_CLEAN_ACTIVITY_SHEET,
} from '../../types/approval';
import {
  calcCleanEvalScore,
  CLEAN_WEEK_COUNT,
  CLEAN_WEEK_LABELS,
  cleanGradeFromScore,
  cleanStatusLabel,
  cleanWeekResultLabel,
  CleanActivityEvalCriterion,
  CleanActivityEvaluation,
  CleanActivitySheet,
  CleanActivitySheetItem,
  createEmptyCleanSheetItem,
  defaultCleanEvalCriteria,
  defaultCleanSheetItems,
  mapSheetItemsForReuse,
  mapSheetItemsToEvalCriteria,
  normalizeWeekResults,
  parseCleanEvalCriteria,
  parseCleanSheetItems,
  serializeCleanSheetItems,
} from '../../types/cleanActivity';
import ApprovalCancelButton from '../approval/ApprovalCancelButton';
import ApprovalRequestModal from '../approval/ApprovalRequestModal';
import PreviousInspectionPickerModal from '../product/PreviousInspectionPickerModal';
import { withApprovalPendingClass } from '../../utils/approvalPending';

import './QualityShell.css';
import '../product/NonconformanceReportPanel.css';
import '../product/InspectionFormSheet.css';
import '../product/PreviousInspectionPickerModal.css';
import './SmallGroupPanel.css';
import './CleanActivityPanel.css';

type Tab = 'sheet' | 'evaluation';
type SheetView =
  | { type: 'list' }
  | { type: 'create' }
  | { type: 'edit'; sheetId: number }
  | { type: 'detail'; sheetId: number };
type EvalView =
  | { type: 'list' }
  | { type: 'create' }
  | { type: 'edit'; evaluationId: number }
  | { type: 'detail'; evaluationId: number };

const SHEET_LIST: SheetView = { type: 'list' };
const EVAL_LIST: EvalView = { type: 'list' };

function today() {
  return new Date().toISOString().slice(0, 10);
}

function newId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function isApproved(status?: string | null) {
  return (status || '').toUpperCase() === 'APPROVED';
}

function StampBox({
  authorName,
  reviewerName,
  approverName,
}: {
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
}) {
  return (
    <div className="ifs-sheet__stamps">
      {(
        [
          ['작성', authorName],
          ['검토', reviewerName],
          ['승인', approverName],
        ] as const
      ).map(([label, value]) => (
        <div key={label}>
          <strong>{label}</strong>
          <span style={{ color: value ? '#1f2a37' : '#98a2b3', fontWeight: value ? 600 : 400 }}>
            {value || '-'}
          </span>
        </div>
      ))}
    </div>
  );
}

function CleanActivityPanel() {
  const companyId = useCompanyStore((s) => s.companyId);
  const role = useCompanyStore((s) => s.role);
  const canApproveDirect = canConfirmInspection(role);
  const [tab, setTab] = useState<Tab>('sheet');
  const [error, setError] = useState('');
  const [approvalMessage, setApprovalMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [myName, setMyName] = useState('');

  const [sheets, setSheets] = useState<CleanActivitySheet[]>([]);
  const [sheetKeyword, setSheetKeyword] = useState('');
  const [sheetView, setSheetView] = useState<SheetView>(SHEET_LIST);
  const [sheetDetail, setSheetDetail] = useState<CleanActivitySheet | null>(null);
  const [sheetItems, setSheetItems] = useState<CleanActivitySheetItem[]>(defaultCleanSheetItems());
  const [sheetForm, setSheetForm] = useState({
    sheetNo: '',
    title: '청정활동 점검시트',
    checkDate: today(),
    areaName: '',
    checkerName: '',
    status: 'DRAFT',
    authorName: '',
    remark: '',
  });
  const [sheetNoHint, setSheetNoHint] = useState('');
  const [sheetApprovalOpen, setSheetApprovalOpen] = useState(false);

  const [evals, setEvals] = useState<CleanActivityEvaluation[]>([]);
  const [evalKeyword, setEvalKeyword] = useState('');
  const [evalView, setEvalView] = useState<EvalView>(EVAL_LIST);
  const [evalDetail, setEvalDetail] = useState<CleanActivityEvaluation | null>(null);
  const [evalCriteria, setEvalCriteria] =
    useState<CleanActivityEvalCriterion[]>(defaultCleanEvalCriteria());
  const [evalForm, setEvalForm] = useState({
    evaluationNo: '',
    title: '청정활동 평가서',
    evaluationDate: today(),
    periodFrom: '',
    periodTo: '',
    targetArea: '',
    evaluator: '',
    scoreText: '',
    gradeText: '',
    strengths: '',
    improvements: '',
    overallComment: '',
    status: 'DRAFT',
    authorName: '',
    remark: '',
  });
  const [evalNoHint, setEvalNoHint] = useState('');
  const [evalApprovalOpen, setEvalApprovalOpen] = useState(false);
  const [sheetImportOpen, setSheetImportOpen] = useState(false);
  const [evalImportOpen, setEvalImportOpen] = useState(false);
  const [loadingImport, setLoadingImport] = useState(false);

  const scoreSummary = useMemo(() => calcCleanEvalScore(evalCriteria), [evalCriteria]);
  const sheetEditingId = sheetView.type === 'edit' ? sheetView.sheetId : null;
  const evalEditingId = evalView.type === 'edit' ? evalView.evaluationId : null;

  const sheetImportCandidates = useMemo(
    () => sheets.filter((row) => row.sheetId !== sheetEditingId),
    [sheets, sheetEditingId]
  );

  const sheetImportPriorityIds = useMemo(() => {
    const area = (sheetForm.areaName || '').trim();
    if (!area) return [] as number[];
    return sheetImportCandidates
      .filter((row) => (row.areaName || '').trim() === area)
      .map((row) => row.sheetId);
  }, [sheetImportCandidates, sheetForm.areaName]);

  const evalImportPriorityIds = useMemo(() => {
    const area = (evalForm.targetArea || '').trim();
    if (!area) return [] as number[];
    return sheets
      .filter((row) => (row.areaName || '').trim() === area)
      .map((row) => row.sheetId);
  }, [sheets, evalForm.targetArea]);

  const sheetImportRows = useMemo(
    () =>
      sheetImportCandidates.map((row) => ({
        id: row.sheetId,
        cells: {
          no: row.sheetNo || '-',
          title: row.title || '-',
          area: row.areaName || '-',
          date: row.checkDate?.slice(0, 10) || '-',
          status: cleanStatusLabel(row.status, row.approvalInProgress),
        },
      })),
    [sheetImportCandidates]
  );

  const evalImportRows = useMemo(
    () =>
      sheets.map((row) => ({
        id: row.sheetId,
        cells: {
          no: row.sheetNo || '-',
          title: row.title || '-',
          area: row.areaName || '-',
          date: row.checkDate?.slice(0, 10) || '-',
          status: cleanStatusLabel(row.status, row.approvalInProgress),
        },
      })),
    [sheets]
  );

  const loadSheets = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      setError('');
      setSheets(await fetchCleanSheets(sheetKeyword));
    } catch {
      reportError(setError, '점검시트 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [companyId, sheetKeyword]);

  const loadEvals = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      setError('');
      setEvals(await fetchCleanEvaluations(evalKeyword));
    } catch {
      reportError(setError, '평가서 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [companyId, evalKeyword]);

  useEffect(() => {
    void fetchMyProfile()
      .then((p) => setMyName((p.nickname || p.loginId || '').trim()))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (tab === 'sheet' && sheetView.type === 'list') void loadSheets();
    if (tab === 'evaluation' && evalView.type === 'list') void loadEvals();
  }, [tab, sheetView.type, evalView.type, loadSheets, loadEvals]);

  useEffect(() => {
    if (sheetView.type !== 'detail') {
      setSheetDetail(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchCleanSheet(sheetView.sheetId)
      .then((row) => {
        if (!cancelled) setSheetDetail(row);
      })
      .catch(() => {
        if (!cancelled) reportError(setError, '점검시트 상세를 불러오지 못했습니다.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sheetView]);

  useEffect(() => {
    if (evalView.type !== 'detail') {
      setEvalDetail(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchCleanEvaluation(evalView.evaluationId)
      .then((row) => {
        if (!cancelled) setEvalDetail(row);
      })
      .catch(() => {
        if (!cancelled) reportError(setError, '평가서 상세를 불러오지 못했습니다.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [evalView]);

  const resetSheetForm = (authorFallback = myName) => {
    setSheetNoHint('');
    setSheetItems(defaultCleanSheetItems());
    setSheetForm({
      sheetNo: '',
      title: '청정활동 점검시트',
      checkDate: today(),
      areaName: '',
      checkerName: authorFallback,
      status: 'DRAFT',
      authorName: authorFallback,
      remark: '',
    });
  };

  const resetEvalForm = (authorFallback = myName) => {
    setEvalNoHint('');
    setEvalCriteria(defaultCleanEvalCriteria());
    setEvalForm({
      evaluationNo: '',
      title: '청정활동 평가서',
      evaluationDate: today(),
      periodFrom: '',
      periodTo: '',
      targetArea: '',
      evaluator: authorFallback,
      scoreText: '',
      gradeText: '',
      strengths: '',
      improvements: '',
      overallComment: '',
      status: 'DRAFT',
      authorName: authorFallback,
      remark: '',
    });
  };

  const openSheetCreate = () => {
    resetSheetForm(myName);
    setSheetNoHint('저장 시 문서번호가 자동 발급됩니다.');
    setSheetView({ type: 'create' });
  };

  const openSheetEdit = (row: CleanActivitySheet) => {
    if (isApproved(row.status)) {
      setSheetView({ type: 'detail', sheetId: row.sheetId });
      return;
    }
    setSheetNoHint('');
    setSheetItems(parseCleanSheetItems(row.itemsJson));
    setSheetForm({
      sheetNo: row.sheetNo || '',
      title: row.title || '청정활동 점검시트',
      checkDate: row.checkDate || today(),
      areaName: row.areaName || '',
      checkerName: row.checkerName || '',
      status: row.status || 'DRAFT',
      authorName: row.authorName || myName,
      remark: row.remark || '',
    });
    setSheetView({ type: 'edit', sheetId: row.sheetId });
  };

  const saveSheet = async (e: FormEvent) => {
    e.preventDefault();
    if (!sheetForm.title.trim()) {
      reportError(setError, '제목을 입력해 주세요.');
      return;
    }
    try {
      setError('');
      const payload = {
        ...sheetForm,
        status: sheetForm.status === 'APPROVED' ? 'COMPLETED' : sheetForm.status || 'DRAFT',
        itemsJson: serializeCleanSheetItems(sheetItems),
      };
      const saved =
        sheetEditingId == null
          ? await createCleanSheet(payload)
          : await updateCleanSheet(sheetEditingId, payload);
      if (sheetEditingId == null) {
        window.alert(`문서번호 ${saved.sheetNo || '-'}로 등록되었습니다.`);
      }
      setSheetView({ type: 'detail', sheetId: saved.sheetId });
      await loadSheets();
    } catch {
      reportError(setError, '점검시트 저장에 실패했습니다.');
    }
  };

  const removeSheet = async (sheetId: number, status?: string | null) => {
    if (isApproved(status)) {
      reportError(setError, '승인 완료된 점검시트는 삭제할 수 없습니다.');
      return;
    }
    if (!window.confirm('이 점검시트를 삭제할까요?')) return;
    try {
      await deleteCleanSheet(sheetId);
      setSheetView({ type: 'list' });
      await loadSheets();
    } catch {
      reportError(setError, '점검시트 삭제에 실패했습니다.');
    }
  };

  const approveSheet = async () => {
    if (!sheetDetail) return;
    if (!canApproveDirect) {
      reportError(setError, '바로 승인은 오너/팀장만 가능합니다.');
      return;
    }
    if (
      !window.confirm(
        '점검시트를 바로 승인할까요? 승인 후에는 수정·삭제할 수 없습니다.'
      )
    ) {
      return;
    }
    try {
      setApproving(true);
      setError('');
      const saved = await approveCleanSheet(sheetDetail.sheetId);
      setSheetDetail(saved);
      setApprovalMessage('바로 승인되었습니다.');
      await loadSheets();
    } catch (err: unknown) {
      reportError(setError, (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '바로 승인에 실패했습니다.');
    } finally {
      setApproving(false);
    }
  };

  const revokeSheet = async () => {
    if (!sheetDetail) return;
    if (!window.confirm('승인을 철회할까요?\n승인란·검토란이 지워집니다.')) return;
    try {
      setApproving(true);
      setError('');
      const saved = await revokeCleanSheet(sheetDetail.sheetId);
      setSheetDetail(saved);
      setApprovalMessage('승인이 철회되었습니다.');
      await loadSheets();
    } catch (err: unknown) {
      reportError(setError, (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '승인 철회에 실패했습니다.');
    } finally {
      setApproving(false);
    }
  };

  const downloadSheetPdf = async () => {
    if (!sheetDetail) return;
    try {
      setPdfDownloading(true);
      const name = sheetDetail.sheetNo?.trim() || String(sheetDetail.sheetId);
      await downloadCleanSheetPdf(sheetDetail.sheetId, `청결점검시트_${name}.pdf`);
    } catch (err) {
      notifyError(err instanceof Error ? err.message : 'PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.');
    } finally {
      setPdfDownloading(false);
    }
  };

  const openEvalCreate = () => {
    resetEvalForm(myName);
    setEvalNoHint('저장 시 문서번호가 자동 발급됩니다.');
    setEvalView({ type: 'create' });
  };

  const openEvalEdit = (row: CleanActivityEvaluation) => {
    if (isApproved(row.status)) {
      setEvalView({ type: 'detail', evaluationId: row.evaluationId });
      return;
    }
    setEvalNoHint('');
    setEvalCriteria(parseCleanEvalCriteria(row.criteriaJson));
    setEvalForm({
      evaluationNo: row.evaluationNo || '',
      title: row.title || '청정활동 평가서',
      evaluationDate: row.evaluationDate || today(),
      periodFrom: row.periodFrom || '',
      periodTo: row.periodTo || '',
      targetArea: row.targetArea || '',
      evaluator: row.evaluator || '',
      scoreText: row.scoreText || '',
      gradeText: row.gradeText || '',
      strengths: row.strengths || '',
      improvements: row.improvements || '',
      overallComment: row.overallComment || '',
      status: row.status || 'DRAFT',
      authorName: row.authorName || myName,
      remark: row.remark || '',
    });
    setEvalView({ type: 'edit', evaluationId: row.evaluationId });
  };

  const saveEval = async (e: FormEvent) => {
    e.preventDefault();
    if (!evalForm.title.trim()) {
      reportError(setError, '제목을 입력해 주세요.');
      return;
    }
    if (evalForm.periodFrom && evalForm.periodTo && evalForm.periodTo < evalForm.periodFrom) {
      reportError(setError, '평가 종료일은 시작일 이후여야 합니다.');
      return;
    }
    try {
      setError('');
      const { sum, max } = calcCleanEvalScore(evalCriteria);
      const scoreText = max > 0 ? `${sum}/${max}` : evalForm.scoreText;
      const gradeText = cleanGradeFromScore(sum, max) || evalForm.gradeText;
      const payload = {
        ...evalForm,
        status: 'DRAFT',
        scoreText,
        gradeText,
        criteriaJson: JSON.stringify(evalCriteria),
      };
      const saved =
        evalEditingId == null
          ? await createCleanEvaluation(payload)
          : await updateCleanEvaluation(evalEditingId, payload);
      if (evalEditingId == null) {
        window.alert(`문서번호 ${saved.evaluationNo || '-'}로 등록되었습니다.`);
      }
      setEvalView({ type: 'detail', evaluationId: saved.evaluationId });
      await loadEvals();
    } catch {
      reportError(setError, '평가서 저장에 실패했습니다.');
    }
  };

  const removeEval = async (evaluationId: number, status?: string | null) => {
    if (isApproved(status)) {
      reportError(setError, '승인 완료된 평가서는 삭제할 수 없습니다.');
      return;
    }
    if (!window.confirm('이 평가서를 삭제할까요?')) return;
    try {
      await deleteCleanEvaluation(evaluationId);
      setEvalView({ type: 'list' });
      await loadEvals();
    } catch {
      reportError(setError, '평가서 삭제에 실패했습니다.');
    }
  };

  const approveEval = async () => {
    if (!evalDetail) return;
    if (!canApproveDirect) {
      reportError(setError, '바로 승인은 오너/팀장만 가능합니다.');
      return;
    }
    if (
      !window.confirm(
        '평가서를 바로 승인할까요? 승인 후에는 수정·삭제할 수 없습니다.'
      )
    ) {
      return;
    }
    try {
      setApproving(true);
      setError('');
      const saved = await approveCleanEvaluation(evalDetail.evaluationId);
      setEvalDetail(saved);
      setApprovalMessage('바로 승인되었습니다.');
      await loadEvals();
    } catch (err: unknown) {
      reportError(setError, (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '바로 승인에 실패했습니다.');
    } finally {
      setApproving(false);
    }
  };

  const revokeEval = async () => {
    if (!evalDetail) return;
    if (!window.confirm('승인을 철회할까요?\n승인란·검토란이 지워집니다.')) return;
    try {
      setApproving(true);
      setError('');
      const saved = await revokeCleanEvaluation(evalDetail.evaluationId);
      setEvalDetail(saved);
      setApprovalMessage('승인이 철회되었습니다.');
      await loadEvals();
    } catch (err: unknown) {
      reportError(setError, (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '승인 철회에 실패했습니다.');
    } finally {
      setApproving(false);
    }
  };

  const downloadEvalPdf = async () => {
    if (!evalDetail) return;
    try {
      setPdfDownloading(true);
      const name = evalDetail.evaluationNo?.trim() || String(evalDetail.evaluationId);
      await downloadCleanEvaluationPdf(evalDetail.evaluationId, `청결평가_${name}.pdf`);
    } catch (err) {
      notifyError(err instanceof Error ? err.message : 'PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.');
    } finally {
      setPdfDownloading(false);
    }
  };

  const patchSheetItem = (idx: number, patch: Partial<CleanActivitySheetItem>) => {
    setSheetItems((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const openSheetImport = async () => {
    setSheetImportOpen(true);
    if (sheets.length === 0) {
      try {
        setLoadingImport(true);
        setSheets(await fetchCleanSheets());
      } catch {
        reportError(setError, '점검시트 목록을 불러오지 못했습니다.');
      } finally {
        setLoadingImport(false);
      }
    }
  };

  const openEvalImport = async () => {
    setEvalImportOpen(true);
    if (sheets.length === 0) {
      try {
        setLoadingImport(true);
        setSheets(await fetchCleanSheets());
      } catch {
        reportError(setError, '점검시트 목록을 불러오지 못했습니다.');
      } finally {
        setLoadingImport(false);
      }
    }
  };

  const applySheetImport = async (sourceSheetId: number) => {
    try {
      setLoadingImport(true);
      setError('');
      const detail = await fetchCleanSheet(sourceSheetId);
      setSheetItems(mapSheetItemsForReuse(parseCleanSheetItems(detail.itemsJson)));
      setSheetImportOpen(false);
    } catch {
      reportError(setError, '이전 점검항목을 불러오지 못했습니다.');
    } finally {
      setLoadingImport(false);
    }
  };

  const applyEvalImport = async (sourceSheetId: number) => {
    try {
      setLoadingImport(true);
      setError('');
      const detail = await fetchCleanSheet(sourceSheetId);
      setEvalCriteria(mapSheetItemsToEvalCriteria(parseCleanSheetItems(detail.itemsJson)));
      setEvalImportOpen(false);
    } catch {
      reportError(setError, '점검항목을 평가항목으로 불러오지 못했습니다.');
    } finally {
      setLoadingImport(false);
    }
  };

  const patchSheetWeekResult = (idx: number, weekIdx: number, value: string) => {
    setSheetItems((prev) =>
      prev.map((r, i) => {
        if (i !== idx) return r;
        const weekResults = normalizeWeekResults(r);
        weekResults[weekIdx] = value;
        return { ...r, weekResults };
      })
    );
  };

  const patchEvalCriterion = (idx: number, patch: Partial<CleanActivityEvalCriterion>) => {
    setEvalCriteria((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const renderSheetItems = (items: CleanActivitySheetItem[], editable: boolean) => (
    <div className="ca-week-table-wrap">
      <table className="ca-week-table">
        <thead>
          <tr>
            <th style={{ width: 40 }}>#</th>
            <th style={{ width: 110 }}>구역</th>
            <th>점검내용</th>
            <th style={{ width: 100 }}>방법</th>
            {CLEAN_WEEK_LABELS.map((label) => (
              <th key={label} style={{ width: 78 }}>
                {label}
              </th>
            ))}
            <th style={{ width: 140 }}>발견사항</th>
            <th style={{ width: 120 }}>비고</th>
            {editable ? <th className="ca-week-table__action" style={{ width: 64 }}>관리</th> : null}
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={editable ? 11 : 10} className="ca-empty">
                등록된 점검 항목이 없습니다.
              </td>
            </tr>
          ) : (
            items.map((item, idx) => {
              const weeks = normalizeWeekResults(item);
              return (
                <tr key={item.id}>
                  <td>{idx + 1}</td>
                  <td className="ca-week-table__cell">
                    {editable ? (
                      <textarea
                        rows={2}
                        value={item.area}
                        onChange={(e) => patchSheetItem(idx, { area: e.target.value })}
                        placeholder="구역"
                      />
                    ) : (
                      <div className="ca-week-table__text">{item.area || '-'}</div>
                    )}
                  </td>
                  <td className="ca-week-table__check">
                    {editable ? (
                      <textarea
                        rows={2}
                        value={item.checkItem}
                        onChange={(e) => patchSheetItem(idx, { checkItem: e.target.value })}
                        placeholder="주별 점검 항목"
                      />
                    ) : (
                      <div className="ca-week-table__text">{item.checkItem || '-'}</div>
                    )}
                  </td>
                  <td className="ca-week-table__cell">
                    {editable ? (
                      <textarea
                        rows={2}
                        value={item.method}
                        onChange={(e) => patchSheetItem(idx, { method: e.target.value })}
                        placeholder="방법"
                      />
                    ) : (
                      <div className="ca-week-table__text">{item.method || '-'}</div>
                    )}
                  </td>
                  {weeks.map((weekValue, weekIdx) => (
                    <td key={`${item.id}-w${weekIdx}`} className="ca-week-table__week">
                      {editable ? (
                        <select
                          value={weekValue}
                          onChange={(e) => patchSheetWeekResult(idx, weekIdx, e.target.value)}
                          aria-label={`${CLEAN_WEEK_LABELS[weekIdx]} 결과`}
                        >
                          <option value="">-</option>
                          <option value="OK">양호</option>
                          <option value="NG">미흡</option>
                          <option value="NA">N/A</option>
                        </select>
                      ) : (
                        <span
                          className={`ca-week-chip is-${(weekValue || 'empty').toLowerCase() || 'empty'}`}
                        >
                          {cleanWeekResultLabel(weekValue)}
                        </span>
                      )}
                    </td>
                  ))}
                  <td className="ca-week-table__note">
                    {editable ? (
                      <textarea
                        rows={2}
                        value={item.finding}
                        onChange={(e) => patchSheetItem(idx, { finding: e.target.value })}
                        placeholder="미흡 시"
                      />
                    ) : (
                      <div className="ca-week-table__text">{item.finding || '-'}</div>
                    )}
                  </td>
                  <td className="ca-week-table__note">
                    {editable ? (
                      <textarea
                        rows={2}
                        value={item.remark}
                        onChange={(e) => patchSheetItem(idx, { remark: e.target.value })}
                      />
                    ) : (
                      <div className="ca-week-table__text">{item.remark || '-'}</div>
                    )}
                  </td>
                  {editable ? (
                    <td className="ca-week-table__action">
                      <button
                        type="button"
                        className="ca-row-del"
                        aria-label={`항목 ${idx + 1} 삭제`}
                        onClick={() => setSheetItems((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        삭제
                      </button>
                    </td>
                  ) : null}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      <p className="quality-form__hint" style={{ marginTop: 8 }}>
        월간 점검 시 항목마다 1~5주차 결과를 한 줄에서 체크합니다. (최대 {CLEAN_WEEK_COUNT}주)
      </p>
    </div>
  );

  const renderEvalCriteria = (
    criteria: CleanActivityEvalCriterion[],
    editable: boolean
  ) => (
    <div className="ca-items">
      {criteria.map((item, idx) => (
        <div className="ca-item-card" key={item.id}>
          <div className="ca-item-card__head">
            <strong>항목 {idx + 1}</strong>
            {editable ? (
              <button
                type="button"
                className="ca-row-del"
                aria-label={`항목 ${idx + 1} 삭제`}
                onClick={() => setEvalCriteria((prev) => prev.filter((_, i) => i !== idx))}
              >
                삭제
              </button>
            ) : null}
          </div>
          <div className="ca-item-card__grid">
            <label className="is-wide">
              평가항목
              <textarea
                rows={2}
                value={item.criterion}
                onChange={(e) => patchEvalCriterion(idx, { criterion: e.target.value })}
                disabled={!editable}
              />
            </label>
            <label>
              배점
              <input
                type="number"
                value={item.maxScore}
                onChange={(e) =>
                  patchEvalCriterion(idx, { maxScore: Number(e.target.value) || 0 })
                }
                disabled={!editable}
              />
            </label>
            <label>
              점수
              <input
                type="number"
                value={item.score}
                onChange={(e) => patchEvalCriterion(idx, { score: e.target.value })}
                disabled={!editable}
              />
            </label>
            <label className="is-full">
              의견
              <textarea
                rows={2}
                value={item.comment}
                onChange={(e) => patchEvalCriterion(idx, { comment: e.target.value })}
                disabled={!editable}
              />
            </label>
          </div>
        </div>
      ))}
      {criteria.length === 0 ? <p className="quality-form__hint">등록된 평가 항목이 없습니다.</p> : null}
    </div>
  );

  const renderSheetDetailActions = () => {
    if (!sheetDetail) return null;
    return (
      <div className="ca-toolbar-actions">
        <button
          type="button"
          className="ncr-btn is-primary"
          disabled={pdfDownloading}
          onClick={() => void downloadSheetPdf()}
        >
          {pdfDownloading ? 'PDF 생성 중…' : 'PDF 다운로드'}
        </button>
        {sheetDetail.canRevoke ? (
          <button
            type="button"
            className="ncr-btn is-danger"
            disabled={approving}
            onClick={() => void revokeSheet()}
          >
            {approving ? '철회 중...' : '승인 철회'}
          </button>
        ) : null}
        {sheetDetail.canCancelApproval ? (
          <ApprovalCancelButton
            detail={sheetDetail}
            className="ncr-btn is-secondary"
            disabled={approving}
            onCancelled={async () => {
              setSheetDetail(await fetchCleanSheet(sheetDetail.sheetId));
              setApprovalMessage('');
              void loadSheets();
            }}
          />
        ) : null}
        {!isApproved(sheetDetail.status) && !sheetDetail.approvalInProgress ? (
          <>
            <button
              type="button"
              className="ncr-btn is-secondary"
              onClick={() => openSheetEdit(sheetDetail)}
            >
              수정
            </button>
            <button
              type="button"
              className="ncr-btn is-primary"
              disabled={approving}
              onClick={() => {
                setApprovalMessage('');
                setSheetApprovalOpen(true);
              }}
            >
              결재 요청
            </button>
            {canApproveDirect ? (
              <button
                type="button"
                className="ncr-btn is-approve"
                disabled={approving}
                onClick={() => void approveSheet()}
              >
                {approving ? '승인 중...' : '승인'}
              </button>
            ) : null}
            <button
              type="button"
              className="ncr-btn is-danger"
              disabled={approving}
              onClick={() => void removeSheet(sheetDetail.sheetId, sheetDetail.status)}
            >
              삭제
            </button>
          </>
        ) : null}
        <button
          type="button"
          className="ncr-btn is-secondary"
          onClick={() => setSheetView({ type: 'list' })}
        >
          목록
        </button>
      </div>
    );
  };

  const renderEvalEditActions = () => (
    <div className="quality-form__actions">
      <button type="submit" className="ncr-btn is-primary">
        저장
      </button>
      <button
        type="button"
        className="ncr-btn is-secondary"
        onClick={() =>
          evalView.type === 'edit'
            ? setEvalView({ type: 'detail', evaluationId: evalView.evaluationId })
            : setEvalView({ type: 'list' })
        }
      >
        {evalView.type === 'edit' ? '상세' : '목록'}
      </button>
    </div>
  );

  const renderEvalDetailActions = () => {
    if (!evalDetail) return null;
    return (
      <div className="ca-toolbar-actions">
        {isApproved(evalDetail.status) ? (
          <button
            type="button"
            className="ncr-btn is-primary"
            disabled={pdfDownloading}
            onClick={() => void downloadEvalPdf()}
          >
            {pdfDownloading ? 'PDF 생성 중…' : 'PDF 다운로드'}
          </button>
        ) : null}
        {evalDetail.canRevoke ? (
          <button
            type="button"
            className="ncr-btn is-danger"
            disabled={approving}
            onClick={() => void revokeEval()}
          >
            {approving ? '철회 중...' : '승인 철회'}
          </button>
        ) : null}
        {evalDetail.canCancelApproval ? (
          <ApprovalCancelButton
            detail={evalDetail}
            className="ncr-btn is-secondary"
            disabled={approving}
            onCancelled={async () => {
              setEvalDetail(await fetchCleanEvaluation(evalDetail.evaluationId));
              setApprovalMessage('');
              void loadEvals();
            }}
          />
        ) : null}
        {!isApproved(evalDetail.status) && !evalDetail.approvalInProgress ? (
          <>
            <button
              type="button"
              className="ncr-btn is-secondary"
              onClick={() => openEvalEdit(evalDetail)}
            >
              수정
            </button>
            <button
              type="button"
              className="ncr-btn is-primary"
              disabled={approving}
              onClick={() => {
                setApprovalMessage('');
                setEvalApprovalOpen(true);
              }}
            >
              결재 요청
            </button>
            {canApproveDirect ? (
              <button
                type="button"
                className="ncr-btn is-approve"
                disabled={approving}
                onClick={() => void approveEval()}
              >
                {approving ? '승인 중...' : '승인'}
              </button>
            ) : null}
            <button
              type="button"
              className="ncr-btn is-danger"
              disabled={approving}
              onClick={() => void removeEval(evalDetail.evaluationId, evalDetail.status)}
            >
              삭제
            </button>
          </>
        ) : null}
        <button
          type="button"
          className="ncr-btn is-secondary"
          onClick={() => setEvalView({ type: 'list' })}
        >
          목록
        </button>
      </div>
    );
  };

  return (
    <main className="quality-shell sg-panel ca-shell">
      <PreviousInspectionPickerModal
        open={sheetImportOpen}
        title="이전 점검항목 불러오기"
        subtitle="선택한 시트의 구역·점검내용·방법만 가져옵니다. 주별 결과와 발견사항은 비워 둡니다. 같은 구역은 위에 표시됩니다."
        emptyText="이전 점검시트가 없습니다."
        loading={loadingImport}
        pageSize={10}
        priorityIds={sheetImportPriorityIds}
        columns={[
          { key: 'no', label: '문서번호' },
          { key: 'title', label: '제목' },
          { key: 'area', label: '구역' },
          { key: 'date', label: '점검일' },
          { key: 'status', label: '상태' },
        ]}
        rows={sheetImportRows}
        onClose={() => setSheetImportOpen(false)}
        onSelect={applySheetImport}
      />
      <PreviousInspectionPickerModal
        open={evalImportOpen}
        title="점검항목 불러오기"
        subtitle="선택한 점검시트의 점검항목을 평가항목으로 가져옵니다. 배점 기본 10, 점수는 비워 둡니다. 같은 구역은 위에 표시됩니다."
        emptyText="불러올 점검시트가 없습니다."
        loading={loadingImport}
        pageSize={10}
        priorityIds={evalImportPriorityIds}
        columns={[
          { key: 'no', label: '문서번호' },
          { key: 'title', label: '제목' },
          { key: 'area', label: '구역' },
          { key: 'date', label: '점검일' },
          { key: 'status', label: '상태' },
        ]}
        rows={evalImportRows}
        onClose={() => setEvalImportOpen(false)}
        onSelect={applyEvalImport}
      />
      <div className="quality-shell__toolbar">
        <div>
          <h2>청정활동</h2>
          <p>구역 점검시트와 평가서를 기록·결재합니다. 저장 후 상세에서 결재를 요청하세요.</p>
        </div>
      </div>

      <div className="quality-tabs">
        {(
          [
            ['sheet', '점검시트'],
            ['evaluation', '평가서'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`quality-tabs__btn${tab === key ? ' is-active' : ''}`}
            onClick={() => {
              setTab(key);
              setSheetView(SHEET_LIST);
              setEvalView(EVAL_LIST);
              setError('');
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {error ? <p className="quality-error">{error}</p> : null}
      {approvalMessage ? <p className="ca-approval-msg">{approvalMessage}</p> : null}
      {loading ? <p className="quality-error ca-loading">불러오는 중...</p> : null}

      {tab === 'sheet' && sheetView.type === 'list' && (
        <>
          <div className="quality-shell__toolbar">
            <input
              className="ca-search"
              value={sheetKeyword}
              onChange={(e) => setSheetKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void loadSheets();
              }}
              placeholder="번호 / 제목 / 구역 / 점검자 검색"
            />
            <div className="ca-toolbar-actions">
              <button type="button" className="ncr-btn is-secondary" onClick={() => void loadSheets()}>
                검색
              </button>
              <button type="button" className="ncr-btn is-primary" onClick={() => void openSheetCreate()}>
                점검시트 등록
              </button>
            </div>
          </div>
          <div className="quality-table-wrap">
            <table className="quality-table">
              <thead>
                <tr>
                  <th>번호</th>
                  <th>제목</th>
                  <th>점검일</th>
                  <th>구역</th>
                  <th>작성</th>
                  <th>상태</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {sheets.length === 0 ? (
                  <tr>
                    <td className="ca-empty" colSpan={7}>
                      등록된 점검시트가 없습니다. 상단에서 등록해 주세요.
                    </td>
                  </tr>
                ) : (
                  sheets.map((row) => (
                    <tr
                      key={row.sheetId}
                      onClick={() => setSheetView({ type: 'detail', sheetId: row.sheetId })}
                    >
                      <td>{row.sheetNo || '-'}</td>
                      <td>{row.title || '-'}</td>
                      <td>{row.checkDate || '-'}</td>
                      <td>{row.areaName || '-'}</td>
                      <td>{row.authorName || '-'}</td>
                      <td>
                        <span
                          className={`quality-badge is-${withApprovalPendingClass(
                            (row.status || 'draft').toLowerCase(),
                            row.approvalInProgress
                          )}`}
                        >
                          {cleanStatusLabel(row.status, row.approvalInProgress)}
                        </span>
                      </td>
                      <td>
                        {!isApproved(row.status) && !row.approvalInProgress ? (
                          <button
                            type="button"
                            className="ncr-btn is-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              void removeSheet(row.sheetId, row.status);
                            }}
                          >
                            삭제
                          </button>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'sheet' && (sheetView.type === 'create' || sheetView.type === 'edit') && (
        <form className="quality-form" onSubmit={saveSheet}>
          <div className="quality-form__actions">
            <button type="submit" className="ncr-btn is-primary">
              저장
            </button>
            <button
              type="button"
              className="ncr-btn is-secondary"
              onClick={() =>
                sheetView.type === 'edit'
                  ? setSheetView({ type: 'detail', sheetId: sheetView.sheetId })
                  : setSheetView({ type: 'list' })
              }
            >
              {sheetView.type === 'edit' ? '상세' : '목록'}
            </button>
          </div>
          <div className="ifs-sheet">
            <div className="ifs-sheet__top">
              <div>
                <h4 className="ifs-sheet__title">
                  {sheetView.type === 'create' ? '점검시트 등록' : '점검시트 수정'}
                </h4>
                <p className="ifs-sheet__subtitle">저장하면 상세보기로 이동합니다.</p>
              </div>
              <StampBox
                authorName={sheetForm.authorName}
                reviewerName={null}
                approverName={null}
              />
            </div>

            <div className="ca-meta-grid">
              <label>
                문서번호
                <input
                  value={sheetView.type === 'create' ? '' : sheetForm.sheetNo || ''}
                  readOnly
                  placeholder={sheetView.type === 'create' ? '저장 시 자동 발급' : undefined}
                />
                {sheetView.type === 'create' && sheetNoHint ? (
                  <span className="quality-form__hint">{sheetNoHint}</span>
                ) : null}
              </label>
              <label>
                점검일
                <input
                  type="date"
                  value={sheetForm.checkDate}
                  onChange={(e) => setSheetForm((p) => ({ ...p, checkDate: e.target.value }))}
                />
              </label>
              <label className="is-span-3">
                제목
                <input
                  value={sheetForm.title}
                  onChange={(e) => setSheetForm((p) => ({ ...p, title: e.target.value }))}
                />
              </label>
              <label>
                작성자
                <input
                  value={sheetForm.authorName}
                  onChange={(e) => setSheetForm((p) => ({ ...p, authorName: e.target.value }))}
                />
              </label>
              <label>
                점검 구역
                <input
                  value={sheetForm.areaName}
                  onChange={(e) => setSheetForm((p) => ({ ...p, areaName: e.target.value }))}
                  placeholder="예: 1공장 조립라인"
                />
              </label>
              <label>
                점검자
                <input
                  value={sheetForm.checkerName}
                  onChange={(e) => setSheetForm((p) => ({ ...p, checkerName: e.target.value }))}
                />
              </label>
            </div>
          </div>

          <div className="quality-form__section">
            <div className="ca-section-head">
              <h3>점검 항목</h3>
              <div className="ca-section-actions">
                <button
                  type="button"
                  className="ncr-btn is-secondary"
                  disabled={loadingImport}
                  onClick={() => void openSheetImport()}
                >
                  이전 점검항목 불러오기
                </button>
                <button
                  type="button"
                  className="ncr-btn is-secondary"
                  onClick={() => setSheetItems((prev) => [...prev, createEmptyCleanSheetItem()])}
                >
                  항목 추가
                </button>
              </div>
            </div>
            {renderSheetItems(sheetItems, true)}
          </div>

          <div className="quality-form__section">
            <div className="quality-form__stack">
              비고
              <textarea
                rows={3}
                value={sheetForm.remark}
                onChange={(e) => setSheetForm((p) => ({ ...p, remark: e.target.value }))}
              />
            </div>
          </div>

          <div className="quality-form__actions">
            <button type="submit" className="ncr-btn is-primary">
              저장
            </button>
            <button
              type="button"
              className="ncr-btn is-secondary"
              onClick={() =>
                sheetView.type === 'edit'
                  ? setSheetView({ type: 'detail', sheetId: sheetView.sheetId })
                  : setSheetView({ type: 'list' })
              }
            >
              {sheetView.type === 'edit' ? '상세' : '목록'}
            </button>
          </div>
        </form>
      )}

      {tab === 'sheet' && sheetView.type === 'detail' && sheetDetail && (
        <div className="quality-form">
          <div className="quality-shell__toolbar">
            <div>
              <h3 style={{ margin: 0 }}>점검시트 상세</h3>
              <p>
                {sheetDetail.sheetNo || '-'} · {cleanStatusLabel(sheetDetail.status, sheetDetail.approvalInProgress)}
              </p>
            </div>
            {renderSheetDetailActions()}
          </div>

          {sheetDetail.approvalInProgress ? (
            <p className="contract-panel__hint">
              결재가 진행 중입니다. 내용을 바꾸려면 결재 요청을 취소한 뒤 수정하고 다시 요청하세요.
            </p>
          ) : null}

          <div className="ifs-sheet">
            <div className="ifs-sheet__top">
              <div>
                <h4 className="ifs-sheet__title">{sheetDetail.title || '청정활동 점검시트'}</h4>
                <p className="ifs-sheet__subtitle">
                  {sheetDetail.checkDate || '-'} · {sheetDetail.areaName || '구역 미입력'}
                </p>
              </div>
              <StampBox
                authorName={sheetDetail.authorName}
                reviewerName={sheetDetail.reviewerName}
                approverName={sheetDetail.approverName}
              />
            </div>
            <div className="ca-meta-grid">
              <label>
                문서번호
                <input readOnly value={sheetDetail.sheetNo || '-'} />
              </label>
              <label>
                점검일
                <input readOnly value={sheetDetail.checkDate || '-'} />
              </label>
              <label className="is-span-2">
                점검 구역
                <input readOnly value={sheetDetail.areaName || '-'} />
              </label>
              <label>
                점검자
                <input readOnly value={sheetDetail.checkerName || '-'} />
              </label>
            </div>
          </div>

          <div className="quality-form__section">
            <h3>점검 항목</h3>
            {renderSheetItems(parseCleanSheetItems(sheetDetail.itemsJson), false)}
          </div>

          <div className="quality-form__section">
            <div className="quality-form__stack">
              비고
              <textarea rows={3} readOnly value={sheetDetail.remark || ''} />
            </div>
          </div>

          <div className="quality-form__actions">{renderSheetDetailActions()}</div>

          <ApprovalRequestModal
            open={sheetApprovalOpen}
            titleDefault={`${sheetDetail.title || '청정활동 점검시트'} 결재`}
            targetType={APPROVAL_TARGET_CLEAN_ACTIVITY_SHEET}
            targetId={sheetDetail.sheetId}
            onClose={() => setSheetApprovalOpen(false)}
            onSubmitted={() => {
              setSheetApprovalOpen(false);
              setApprovalMessage(
                '결재 요청이 등록되었습니다. 헤더의 결재 탭에서 확인할 수 있습니다.'
              );
              setSheetView({ type: 'detail', sheetId: sheetDetail.sheetId });
              void fetchCleanSheet(sheetDetail.sheetId).then(setSheetDetail);
              void loadSheets();
            }}
          />
        </div>
      )}

      {tab === 'evaluation' && evalView.type === 'list' && (
        <>
          <div className="quality-shell__toolbar">
            <input
              className="ca-search"
              value={evalKeyword}
              onChange={(e) => setEvalKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void loadEvals();
              }}
              placeholder="번호 / 제목 / 대상구역 / 평가자 검색"
            />
            <div className="ca-toolbar-actions">
              <button type="button" className="ncr-btn is-secondary" onClick={() => void loadEvals()}>
                검색
              </button>
              <button type="button" className="ncr-btn is-primary" onClick={() => void openEvalCreate()}>
                평가서 등록
              </button>
            </div>
          </div>
          <div className="quality-table-wrap">
            <table className="quality-table">
              <thead>
                <tr>
                  <th>번호</th>
                  <th>제목</th>
                  <th>평가일</th>
                  <th>점수</th>
                  <th>작성</th>
                  <th>상태</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {evals.length === 0 ? (
                  <tr>
                    <td className="ca-empty" colSpan={7}>
                      등록된 평가서가 없습니다. 상단에서 등록해 주세요.
                    </td>
                  </tr>
                ) : (
                  evals.map((row) => (
                    <tr
                      key={row.evaluationId}
                      onClick={() =>
                        setEvalView({ type: 'detail', evaluationId: row.evaluationId })
                      }
                    >
                      <td>{row.evaluationNo || '-'}</td>
                      <td>{row.title || '-'}</td>
                      <td>{row.evaluationDate || '-'}</td>
                      <td>
                        {row.scoreText || '-'}
                        {row.gradeText ? ` · ${row.gradeText}` : ''}
                      </td>
                      <td>{row.authorName || '-'}</td>
                      <td>
                        <span
                          className={`quality-badge is-${withApprovalPendingClass(
                            (row.status || 'draft').toLowerCase(),
                            row.approvalInProgress
                          )}`}
                        >
                          {cleanStatusLabel(row.status, row.approvalInProgress)}
                        </span>
                      </td>
                      <td>
                        {!isApproved(row.status) && !row.approvalInProgress ? (
                          <button
                            type="button"
                            className="ncr-btn is-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              void removeEval(row.evaluationId, row.status);
                            }}
                          >
                            삭제
                          </button>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'evaluation' && (evalView.type === 'create' || evalView.type === 'edit') && (
        <form className="quality-form" onSubmit={saveEval}>
          {renderEvalEditActions()}
          <div className="ifs-sheet">
            <div className="ifs-sheet__top">
              <div>
                <h4 className="ifs-sheet__title">
                  {evalView.type === 'create' ? '평가서 등록' : '평가서 수정'}
                </h4>
                <p className="ifs-sheet__subtitle">저장하면 상세보기로 이동합니다.</p>
              </div>
              <StampBox
                authorName={evalForm.authorName}
                reviewerName={null}
                approverName={null}
              />
            </div>
            <div className="ca-meta-grid">
              <label>
                문서번호
                <input
                  value={evalView.type === 'create' ? '' : evalForm.evaluationNo || ''}
                  readOnly
                  placeholder={evalView.type === 'create' ? '저장 시 자동 발급' : undefined}
                />
                {evalView.type === 'create' && evalNoHint ? (
                  <span className="quality-form__hint">{evalNoHint}</span>
                ) : null}
              </label>
              <label>
                평가일
                <input
                  type="date"
                  value={evalForm.evaluationDate}
                  onChange={(e) => setEvalForm((p) => ({ ...p, evaluationDate: e.target.value }))}
                />
              </label>
              <label className="is-span-3">
                제목
                <input
                  value={evalForm.title}
                  onChange={(e) => setEvalForm((p) => ({ ...p, title: e.target.value }))}
                />
              </label>
              <label>
                작성자
                <input
                  value={evalForm.authorName}
                  onChange={(e) => setEvalForm((p) => ({ ...p, authorName: e.target.value }))}
                />
              </label>
              <label>
                평가기간 시작
                <input
                  type="date"
                  value={evalForm.periodFrom}
                  onChange={(e) => setEvalForm((p) => ({ ...p, periodFrom: e.target.value }))}
                />
              </label>
              <label>
                평가기간 종료
                <input
                  type="date"
                  value={evalForm.periodTo}
                  onChange={(e) => setEvalForm((p) => ({ ...p, periodTo: e.target.value }))}
                />
              </label>
              <label className="is-span-2">
                대상 구역
                <input
                  value={evalForm.targetArea}
                  onChange={(e) => setEvalForm((p) => ({ ...p, targetArea: e.target.value }))}
                />
              </label>
              <label>
                평가자
                <input
                  value={evalForm.evaluator}
                  onChange={(e) => setEvalForm((p) => ({ ...p, evaluator: e.target.value }))}
                />
              </label>
              <label className="is-span-3">
                자동 집계
                <input
                  readOnly
                  value={
                    scoreSummary.max > 0
                      ? `${scoreSummary.sum}/${scoreSummary.max} · ${cleanGradeFromScore(
                          scoreSummary.sum,
                          scoreSummary.max
                        )}`
                      : '-'
                  }
                />
              </label>
            </div>
          </div>

          <div className="quality-form__section">
            <div className="ca-section-head">
              <h3>평가 항목</h3>
              <div className="ca-section-actions">
                <button
                  type="button"
                  className="ncr-btn is-secondary"
                  disabled={loadingImport}
                  onClick={() => void openEvalImport()}
                >
                  점검항목 불러오기
                </button>
                <button
                  type="button"
                  className="ncr-btn is-secondary"
                  onClick={() =>
                    setEvalCriteria((prev) => [
                      ...prev,
                      { id: newId(), criterion: '', maxScore: 10, score: '', comment: '' },
                    ])
                  }
                >
                  항목 추가
                </button>
              </div>
            </div>
            {renderEvalCriteria(evalCriteria, true)}
          </div>

          <div className="quality-form__section">
            <div className="ca-meta-grid">
              <div className="quality-form__stack is-span-3">
                잘된 점
                <textarea
                  rows={2}
                  value={evalForm.strengths}
                  onChange={(e) => setEvalForm((p) => ({ ...p, strengths: e.target.value }))}
                />
              </div>
              <div className="quality-form__stack is-span-3">
                개선 필요 사항
                <textarea
                  rows={2}
                  value={evalForm.improvements}
                  onChange={(e) => setEvalForm((p) => ({ ...p, improvements: e.target.value }))}
                />
              </div>
              <div className="quality-form__stack is-span-3">
                종합 의견
                <textarea
                  rows={3}
                  value={evalForm.overallComment}
                  onChange={(e) => setEvalForm((p) => ({ ...p, overallComment: e.target.value }))}
                />
              </div>
              <div className="quality-form__stack is-span-3">
                비고
                <textarea
                  rows={2}
                  value={evalForm.remark}
                  onChange={(e) => setEvalForm((p) => ({ ...p, remark: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {renderEvalEditActions()}
        </form>
      )}

      {tab === 'evaluation' && evalView.type === 'detail' && evalDetail && (
        <div className="quality-form">
          <div className="quality-shell__toolbar">
            <div>
              <h3 style={{ margin: 0 }}>평가서 상세</h3>
              <p>
                {evalDetail.evaluationNo || '-'} · {cleanStatusLabel(evalDetail.status, evalDetail.approvalInProgress)}
              </p>
            </div>
            {renderEvalDetailActions()}
          </div>

          {evalDetail.approvalInProgress ? (
            <p className="contract-panel__hint">
              결재가 진행 중입니다. 내용을 바꾸려면 결재 요청을 취소한 뒤 수정하고 다시 요청하세요.
            </p>
          ) : null}

          <div className="ifs-sheet">
            <div className="ifs-sheet__top">
              <div>
                <h4 className="ifs-sheet__title">{evalDetail.title || '청정활동 평가서'}</h4>
                <p className="ifs-sheet__subtitle">
                  {evalDetail.evaluationDate || '-'} · {evalDetail.targetArea || '대상구역 미입력'}
                </p>
              </div>
              <StampBox
                authorName={evalDetail.authorName}
                reviewerName={evalDetail.reviewerName}
                approverName={evalDetail.approverName}
              />
            </div>
            <div className="ca-meta-grid">
              <label>
                문서번호
                <input readOnly value={evalDetail.evaluationNo || '-'} />
              </label>
              <label>
                평가일
                <input readOnly value={evalDetail.evaluationDate || '-'} />
              </label>
              <label>
                평가기간
                <input
                  readOnly
                  value={
                    evalDetail.periodFrom || evalDetail.periodTo
                      ? `${evalDetail.periodFrom || '-'} ~ ${evalDetail.periodTo || '-'}`
                      : '-'
                  }
                />
              </label>
              <label>
                대상 구역
                <input readOnly value={evalDetail.targetArea || '-'} />
              </label>
              <label>
                평가자
                <input readOnly value={evalDetail.evaluator || '-'} />
              </label>
              <label className="is-span-2">
                점수 / 등급
                <input
                  readOnly
                  value={
                    `${evalDetail.scoreText || '-'}${
                      evalDetail.gradeText ? ` · ${evalDetail.gradeText}` : ''
                    }`
                  }
                />
              </label>
            </div>
          </div>

          <div className="quality-form__section">
            <h3>평가 항목</h3>
            {renderEvalCriteria(parseCleanEvalCriteria(evalDetail.criteriaJson), false)}
          </div>

          <div className="quality-form__section">
            <div className="ca-meta-grid">
              <div className="quality-form__stack is-span-3">
                잘된 점
                <textarea rows={2} readOnly value={evalDetail.strengths || ''} />
              </div>
              <div className="quality-form__stack is-span-3">
                개선 필요 사항
                <textarea rows={2} readOnly value={evalDetail.improvements || ''} />
              </div>
              <div className="quality-form__stack is-span-3">
                종합 의견
                <textarea rows={3} readOnly value={evalDetail.overallComment || ''} />
              </div>
              <div className="quality-form__stack is-span-3">
                비고
                <textarea rows={2} readOnly value={evalDetail.remark || ''} />
              </div>
            </div>
          </div>

          <div className="quality-form__actions">{renderEvalDetailActions()}</div>

          <ApprovalRequestModal
            open={evalApprovalOpen}
            titleDefault={`${evalDetail.title || '청정활동 평가서'} 결재`}
            targetType={APPROVAL_TARGET_CLEAN_ACTIVITY_EVALUATION}
            targetId={evalDetail.evaluationId}
            onClose={() => setEvalApprovalOpen(false)}
            onSubmitted={() => {
              setEvalApprovalOpen(false);
              void fetchCleanEvaluation(evalDetail.evaluationId).then(setEvalDetail);
              void loadEvals();
            }}
          />
        </div>
      )}
    </main>
  );
}

export default CleanActivityPanel;
